import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { FiInfo } from 'react-icons/fi'
import { TicketStatusBadge } from '../TicketStatusCard'
import { TicketStatus } from '../TicketStatusCard/TicketStatusBadge.types'
import Tooltip from '../Tooltip'
import { isEmptyRewardResult } from '../../utils/rewardResultUtils'
import {
  isSetLotteryBetType,
  formatMatchType,
} from '../../types/setLottery.types'
import { computeFinalPayout } from './computeFinalPayout'
import type { BetDetailItem } from './TicketDetails'
import WinningNumbersCollapsible from './WinningNumbersCollapsible'
import clsx from 'clsx'

export interface BetDetailsTableProps {
  betDetails: BetDetailItem[]
}

/** Format reward result for display in table reward section (e.g. "582 · 528 · 285") */
function formatRewardResult(value: number | string | undefined): string {
  if (value === undefined || value === null) return '—'
  const s = String(value).trim()
  if (!s) return '—'

  const parts = s.split(/[,\s]+/).filter(Boolean)
  if (parts.length === 0) return '—'

  return parts.join(' · ')
}

/** Group bet details by bet type for one table per type */
function groupByBetType(items: BetDetailItem[]): Map<string, BetDetailItem[]> {
  const map = new Map<string, BetDetailItem[]>()
  for (const item of items) {
    const key = item.betType
    const list = map.get(key) ?? []
    list.push(item)
    map.set(key, list)
  }
  return map
}

/** Sub-component for a single bet type group table section */
const BetTypeGroupSection: React.FC<{
  betType: string
  rows: BetDetailItem[]
}> = ({ betType, rows }) => {
  const isGroupSetLottery = rows.some((r) => isSetLotteryBetType(r.rawBetType))
  const { t } = useTranslation()

  // Collect winning numbers from the first row that has them (shared per group)
  const winningNumbers = rows.find((r) => r.winningNumbers)?.winningNumbers

  const thStyle = clsx(
    'px-3 sm:px-5 py-2.5 text-center font-semibold text-xs uppercase tracking-wide text-[var(--bet-detail-row-label)] bg-[var(--bet-detail-card-bg)] border-b border-[var(--ticket-info-divider-subtle)]',
    !isGroupSetLottery && 'sm:text-left',
  )

  return (
    <section className="w-full rounded-xl overflow-hidden bg-[var(--bet-detail-card-bg)] border border-[var(--ticket-info-divider-subtle)] shadow-sm">
      {/* Header: active-gradient adapts to blue and gold theme */}
      <div className="w-full py-3 px-4 sm:px-6 text-center font-bold text-base leading-normal border-none active-gradient text-[var(--tab-text-active)]">
        {betType}
      </div>

      {/* Table */}
      <div className="w-full overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr>
              <th className={thStyle}>{t('pollDetails.betList', 'Bet #')}</th>
              <th className={thStyle}>
                {t('pollDetails.betAmount', 'Bet Amount')}
              </th>
              <th className={thStyle}>
                {isGroupSetLottery
                  ? t('pollDetails.maxRewardAmount', 'Max Reward Amount')
                  : t('pollDetails.payoutRate', 'Payout Rate')}
              </th>
              {!isGroupSetLottery && (
                <th className={clsx(thStyle, 'hidden sm:table-cell')}>
                  {t('pollDetails.finalPayout', 'Final Payout')}
                </th>
              )}
              <th className={thStyle}>{t('pollDetails.status', 'Status')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const hasLimitedRate =
                row.limitedPayoutRate !== undefined &&
                row.limitedPayoutRate !== row.payoutRate
              const effectiveRate = hasLimitedRate
                ? row.limitedPayoutRate!
                : row.payoutRate
              const isWin = row.status === TicketStatus.WIN
              const isLose = row.status === TicketStatus.LOSE
              const isLastRow = idx === rows.length - 1
              const finalPayoutClass = isWin
                ? 'text-[var(--color-success-600)]'
                : isLose
                  ? 'text-[var(--color-error-600)]'
                  : 'text-[var(--bet-detail-value)]'

              const computedFinalPayout = computeFinalPayout(row)

              const tdBase = clsx(
                'px-3 sm:px-5 py-3 border-b border-[var(--ticket-info-divider-subtle)] text-center text-[var(--bet-detail-value)]',
                !isGroupSetLottery && 'sm:text-left',
              )

              return (
                <tr
                  key={`${row.betNumber}-${idx}`}
                  className="hover:bg-[var(--bet-detail-card-bg)]"
                >
                  <td
                    className={clsx(
                      tdBase,
                      'font-bold text-[var(--bet-detail-number)]',
                      isLastRow && 'border-b-0',
                    )}
                  >
                    {row.betNumber}
                  </td>
                  <td className={clsx(tdBase, isLastRow && 'border-b-0')}>
                    {row.betAmount.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </td>
                  <td className={clsx(tdBase, isLastRow && 'border-b-0')}>
                    {isGroupSetLottery && row.maxRewardAmount !== undefined ? (
                      <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
                        <span className="text-[var(--bet-detail-value)]">
                          {row.maxRewardAmount.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                        <Tooltip
                          content={t(
                            'pollDetails.maxRewardTooltip',
                            'Maximum payout if your ticket wins the highest prize tier.',
                          )}
                        >
                          <FiInfo
                            className="w-4 h-4 shrink-0 text-[var(--ticket-info-card-top-border)] cursor-pointer align-middle hidden sm:inline-block"
                            aria-hidden
                          />
                        </Tooltip>
                      </div>
                    ) : (
                      <>
                        <div
                          className={clsx(
                            'flex flex-wrap items-center justify-center gap-x-2 gap-y-1',
                            'sm:justify-start',
                          )}
                        >
                          {hasLimitedRate && (
                            <span className="line-through text-[var(--bet-detail-strikethrough)] opacity-20 text-inherit">
                              {row.payoutRate.toLocaleString('en-US', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </span>
                          )}
                          <span className="text-[var(--bet-detail-value)]">
                            {effectiveRate.toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </span>
                          <Tooltip
                            content={
                              <>
                                {t('pollDetails.payoutTooltipPrefix')}{' '}
                                <span
                                  style={{
                                    color: 'var(--tooltip-highlight)',
                                    fontWeight: 600,
                                  }}
                                >
                                  {t('pollDetails.payoutTooltipHighlight')}
                                </span>
                                {t('pollDetails.payoutTooltipSuffix')}
                              </>
                            }
                          >
                            <FiInfo
                              className="w-4 h-4 shrink-0 text-[var(--ticket-info-card-top-border)] cursor-pointer align-middle hidden sm:inline-block"
                              aria-hidden
                            />
                          </Tooltip>
                        </div>
                        {/* Mobile only: computed final payout below rate (only for Win status) */}
                        {isWin && (
                          <div className="sm:hidden mt-1.5 flex items-center justify-center gap-1">
                            <span className={finalPayoutClass}>
                              (
                              {computedFinalPayout.toLocaleString('en-US', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                              )
                            </span>
                            <Tooltip
                              content={
                                <>
                                  {t('pollDetails.payoutTooltipPrefix')}{' '}
                                  <span
                                    style={{
                                      color: 'var(--tooltip-highlight)',
                                      fontWeight: 600,
                                    }}
                                  >
                                    {t('pollDetails.payoutTooltipHighlight')}
                                  </span>
                                  {t('pollDetails.payoutTooltipSuffix')}
                                </>
                              }
                            >
                              <FiInfo
                                className="w-4 h-4 shrink-0 text-[var(--ticket-info-card-top-border)] cursor-pointer align-middle"
                                aria-hidden
                              />
                            </Tooltip>
                          </div>
                        )}
                      </>
                    )}
                  </td>
                  {!isGroupSetLottery && (
                    <td
                      className={clsx(
                        tdBase,
                        'hidden sm:table-cell',
                        isLastRow && 'border-b-0',
                      )}
                    >
                      <span className={finalPayoutClass}>
                        {computedFinalPayout.toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </td>
                  )}
                  <td className={clsx(tdBase, isLastRow && 'border-b-0')}>
                    <div
                      className={clsx(
                        'flex justify-center',
                        !isGroupSetLottery && 'sm:justify-start',
                      )}
                    >
                      {isGroupSetLottery && row.matchType ? (
                        <span
                          className={clsx(
                            'inline-block px-2.5 py-1 text-xs font-medium rounded-full whitespace-nowrap',
                            row.matchType === 'no_match'
                              ? 'bg-[var(--set-lottery-badge-lose-bg)] text-[var(--set-lottery-badge-lose-text)]'
                              : 'bg-[var(--set-lottery-badge-win-bg)] text-[var(--set-lottery-badge-win-text)]',
                          )}
                        >
                          {formatMatchType(row.matchType)}
                        </span>
                      ) : (
                        <TicketStatusBadge status={row.status} />
                      )}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Reward Result section */}
      <div className="p-4 pb-5 border-t border-[var(--ticket-info-divider-subtle)]">
        <div className="flex items-center gap-3 mb-3">
          <span
            className="flex-1 h-px bg-[var(--ticket-info-divider)] opacity-30"
            aria-hidden
          />
          <span className="shrink-0 text-xs font-medium text-[var(--bet-detail-row-label)] uppercase tracking-wide">
            {t('pollDetails.rewardResult', 'Reward Result').toUpperCase()}
          </span>
          <span
            className="flex-1 h-px bg-[var(--ticket-info-divider)] opacity-30"
            aria-hidden
          />
        </div>

        {/* Reward result outer container */}
        <div
          className="rounded-lg bg-[var(--reward-result-group-bg)] px-3 py-2.5"
          style={{
            border: '1px solid var(--reward-result-group-border)',
          }}
        >
          {/* Prize number */}
          <RewardResultDisplay
            rows={rows}
            isGroupSetLottery={isGroupSetLottery}
          />

          {/* Collapsible toggle (set lottery, hidden when all rows are waiting) */}
          {isGroupSetLottery &&
            winningNumbers &&
            winningNumbers.length > 0 &&
            !rows.every((r) => r.status === TicketStatus.WAITING) && (
              <WinningNumbersCollapsible
                winningNumbers={winningNumbers}
                className="mt-3"
              />
            )}
        </div>
      </div>
    </section>
  )
}

/** Sub-component for the reward result display in table view */
const RewardResultDisplay: React.FC<{
  rows: BetDetailItem[]
  isGroupSetLottery: boolean
}> = ({ rows, isGroupSetLottery }) => {
  const { t } = useTranslation()
  const first = rows[0]
  const hasNoResult = isEmptyRewardResult(first.rewardResult)
  const showWaitingBadge = hasNoResult && first.status === TicketStatus.WAITING

  return (
    <div className="flex flex-col items-center justify-center min-h-[2.5rem] py-2">
      {showWaitingBadge ? (
        <TicketStatusBadge status={TicketStatus.WAITING} />
      ) : !hasNoResult ? (
        <>
          {isGroupSetLottery && (
            <span className="text-xs text-[var(--bet-detail-row-label)] mb-1">
              {t('setLottery.firstPrize', '1st Prize')}
            </span>
          )}
          <span
            className="text-base sm:text-lg font-bold tracking-[0.08em] whitespace-nowrap"
            style={{ color: 'var(--reward-result-accent)' }}
          >
            {isGroupSetLottery
              ? String(first.rewardResult).split('').join(' ')
              : formatRewardResult(first.rewardResult)}
          </span>
        </>
      ) : (
        <span className="text-[var(--bet-detail-strikethrough)]">—</span>
      )}
    </div>
  )
}

const BetDetailsTable: React.FC<BetDetailsTableProps> = ({ betDetails }) => {
  const groups = useMemo(
    () => Array.from(groupByBetType(betDetails).entries()),
    [betDetails],
  )

  if (groups.length === 0) return null

  return (
    <div className="w-full max-w-5xl flex flex-col gap-6">
      {groups.map(([betType, rows]) => (
        <BetTypeGroupSection key={betType} betType={betType} rows={rows} />
      ))}
    </div>
  )
}

export default BetDetailsTable
